/** @jsxImportSource @emotion/react */
'use client';

import React, { useId } from 'react';
import { Circle, HiddenInput, LabelText, Root } from './styled';
import type { RadioProps } from './types';

export const Radio = React.forwardRef<HTMLInputElement, RadioProps>(
  (
    {
      variant = 'default',
      size = 'md',
      checked,
      defaultChecked,
      disabled = false,
      label,
      children,
      className,
      testId = 'radio',
      css,
      style,
      onChange,
      onCheckedChange,
      id,
      name,
      ...rest
    },
    ref
  ) => {
    const generatedId = useId();
    const inputId = id ?? generatedId;
    const inputRef = React.useRef<HTMLInputElement | null>(null);
    const isControlled = checked !== undefined;
    const [internalChecked, setInternalChecked] = React.useState(
      Boolean(defaultChecked)
    );
    const isChecked = isControlled ? Boolean(checked) : internalChecked;

    const setRefs = React.useCallback(
      (node: HTMLInputElement | null) => {
        inputRef.current = node;
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
      },
      [ref]
    );

    // Uncontrolled radio groups: siblings uncheck natively without React re-render.
    // Sync visual/data state when any radio in the same name group changes.
    React.useEffect(() => {
      if (isControlled) return;
      const input = inputRef.current;
      if (!input) return;

      const syncFromDom = () => {
        setInternalChecked(input.checked);
      };

      const onGroupChange = (event: Event) => {
        const target = event.target;
        if (!(target instanceof HTMLInputElement)) return;
        if (target.type !== 'radio') return;
        if (!input.name || target.name !== input.name) return;
        syncFromDom();
      };

      document.addEventListener('change', onGroupChange);
      return () => document.removeEventListener('change', onGroupChange);
    }, [isControlled, name]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!isControlled) {
        setInternalChecked(e.target.checked);
      }
      onChange?.(e);
      onCheckedChange?.(e.target.checked);
    };

    return (
      <Root
        htmlFor={inputId}
        $variant={variant}
        $checked={isChecked}
        $disabled={disabled}
        className={className}
        css={css}
        style={style}
        data-testid={testId}
        data-variant={variant}
        data-checked={isChecked || undefined}
      >
        {variant === 'card' && (label || children) ? (
          <LabelText>{label ?? children}</LabelText>
        ) : null}
        <HiddenInput
          ref={setRefs}
          id={inputId}
          type="radio"
          name={name}
          {...rest}
          checked={isControlled ? checked : undefined}
          defaultChecked={!isControlled ? defaultChecked : undefined}
          disabled={disabled}
          onChange={handleChange}
        />
        <Circle $size={size} $checked={isChecked} aria-hidden />
        {variant !== 'card' && (label || children) ? (
          <LabelText>{label ?? children}</LabelText>
        ) : null}
      </Root>
    );
  }
);

Radio.displayName = 'Radio';
